import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

export type CompetitorTier = 'owned' | 'aspirational' | 'ambient' | 'adjacent' | 'anti' | 'unclassified' | null;

export interface CompetitorFinderTopUrl {
  url: string;
  title?: string;
  position?: number;
  entitiesCount?: number;
}

export interface CompetitorFinderIntersectionBandCounts {
  bandA: number;
  bandB: number;
  bandC: number;
  bandD: number;
}

export interface CompetitorFinderIntersectionSample {
  keyword: string;
  firstRank: number | null;
  secondRank: number | null;
  band: 'A' | 'B' | 'C' | 'D';
  searchVolume?: number | null;
}

export interface CompetitorFinderMatchDetail {
  keyword: string;
  position: number | null;
}

export interface InputKeywordPerformance {
  inputKeywords: string[];
  matchedRankedKeywords: number;
  avgPosition: number | null;
  totalSearchVolume: number;
  totalEstimatedTraffic: number;
  byInputKeyword: Array<{
    keyword: string;
    matchedRankedKeywords: number;
    avgPosition: number | null;
    totalSearchVolume: number;
    totalEstimatedTraffic: number;
  }>;
}

export interface CompetitorFinderRow {
  domain: string;
  score: number;
  discoverySources?: string[];
  matchKeywords?: string[];
  matchDetails?: CompetitorFinderMatchDetail[];
  intersections: number;
  weightedIntersections: number | null;
  sitemapPages?: number | null;
  intersectionBandCounts?: CompetitorFinderIntersectionBandCounts | null;
  intersectionSamples?: CompetitorFinderIntersectionSample[];
  avgPosition: number | null;
  coveragePercentage: number | null;
  organicTraffic?: number | null;
  domainRating?: number | null;
  entitiesCovered: number | null;
  totalEntities: number | null;
  authorityScore: number | null;
  topicTier: CompetitorTier;
  topUrls: CompetitorFinderTopUrl[];
  lastUpdated: string;
  dataSource: 'database' | 'api';
}

export interface CompetitorFinderResponse {
  success: boolean;
  data: {
    domain: string;
    rows: CompetitorFinderRow[];
    assistedRows?: CompetitorFinderRow[];
    stats: {
      totalCandidates: number;
      totalRows: number;
      excludedByDenylist?: number;
      excludedBySitemapSize?: number;
      sitemapCheckTimeouts?: number;
    };
    assistedStats?: {
      totalCandidates: number;
      totalRows: number;
    };
    costBreakdown: {
      competitorsDomain: number;
      serpCompetitors?: number;
      rankedKeywords?: number;
      domainIntersection?: number;
      total: number;
    };
  };
  metadata: {
    dataSource: 'database' | 'api';
    lastUpdated: string | null;
    locationCode: number;
    languageName: string;
    maxDomainSitemapPages?: number | null;
    pageAnalysisAssistedAvailable?: boolean;
    searchPlan?: {
      source: 'serp_competitors' | 'ranked_keywords';
      topicsUsed: string[];
      keywords: string[];
    };
    topicPool?: string[];
    estimatedCosts?: {
      competitorsDomain: number;
      serpCompetitors: number;
      rankedKeywords: number;
      domainIntersection: number;
      total: number;
    };
    inputKeywordPerformance?: InputKeywordPerformance | null;
    runId?: string;
    runRecordId?: string;
    cacheMissReason?: string | null;
  };
  timestamp: string;
}

export interface CompetitorFinderProgressResponse {
  success: boolean;
  data: {
    runId: string;
    phase: string;
    message: string;
    percent: number;
    status: 'running' | 'done' | 'error';
    updatedAt: string;
    error?: string | null;
  } | null;
  timestamp: string;
}

export interface CompetitorFinderPreviewResponse {
  success: boolean;
  data: {
    pageAnalysisAssistedAvailable: boolean;
    searchPlan: {
      source: 'serp_competitors' | 'ranked_keywords';
      topicsUsed: string[];
      keywords: string[];
    };
    topicPool?: string[];
    estimatedCalls: {
      competitorsDomain: number;
      serpCompetitors: number;
      rankedKeywords: number;
      domainIntersection: number;
    };
    estimatedCosts: {
      competitorsDomain: number;
      serpCompetitors: number;
      rankedKeywords: number;
      domainIntersection: number;
      total: number;
    };
  };
  timestamp: string;
}

export interface CompetitorFinderRunListItem {
  id: string;
  domain: string;
  locationCode: number;
  languageName: string;
  limit: number;
  maxDomainSitemapPages: number | null;
  rowCount: number;
  assistedRowCount: number;
  totalCost: number;
  lastUpdated: string;
  createdAt: string;
}

export interface CompetitorFinderRunsResponse {
  success: boolean;
  data: {
    runs: CompetitorFinderRunListItem[];
  };
  timestamp: string;
}

export interface RemoveCompetitorFinderRowResponse {
  success: boolean;
  data: {
    runId: string;
    domain: string;
    listType: 'rows' | 'assistedRows';
    rows: number;
    assistedRows: number;
  };
  timestamp: string;
}

@Injectable({
  providedIn: 'root'
})
export class CompetitorFinderService {
  private readonly API_URL = environment.apiUrl + '/v1';

  constructor(private http: HttpClient) {}

  runCompetitorFinder(payload: {
    domain: string;
    locationCode?: number;
    languageName?: string;
    limit?: number;
    forceRefresh?: boolean;
    maxDomainSitemapPages?: number | null;
    assistedTopicsLimit?: number;
    customAssistedTopics?: string[];
    runId?: string;
  }): Observable<CompetitorFinderResponse> {
    return this.http.post<CompetitorFinderResponse>(
      `${this.API_URL}/dataforseo/competitor-finder/run`,
      payload
    );
  }

  previewCompetitorFinder(payload: {
    domain: string;
    limit?: number;
    assistedTopicsLimit?: number;
    customAssistedTopics?: string[];
  }): Observable<CompetitorFinderPreviewResponse> {
    return this.http.post<CompetitorFinderPreviewResponse>(
      `${this.API_URL}/dataforseo/competitor-finder/preview`,
      payload
    );
  }

  getCompetitorFinderProgress(runId: string): Observable<CompetitorFinderProgressResponse> {
    return this.http.get<CompetitorFinderProgressResponse>(
      `${this.API_URL}/dataforseo/competitor-finder/progress/${encodeURIComponent(runId)}`
    );
  }

  getCompetitorFinderRuns(params?: {
    domain?: string;
    limit?: number;
  }): Observable<CompetitorFinderRunsResponse> {
    const query = new URLSearchParams();
    if (params?.domain) query.set('domain', params.domain);
    if (params?.limit != null) query.set('limit', String(params.limit));
    const queryString = query.toString();
    return this.http.get<CompetitorFinderRunsResponse>(
      `${this.API_URL}/dataforseo/competitor-finder/runs${queryString ? `?${queryString}` : ''}`
    );
  }

  getCompetitorFinderRun(runId: string): Observable<CompetitorFinderResponse> {
    return this.http.get<CompetitorFinderResponse>(
      `${this.API_URL}/dataforseo/competitor-finder/runs/${encodeURIComponent(runId)}`
    );
  }

  removeCompetitorFinderRow(runId: string, domain: string, listType: 'rows' | 'assistedRows'): Observable<RemoveCompetitorFinderRowResponse> {
    return this.http.post<RemoveCompetitorFinderRowResponse>(
      `${this.API_URL}/dataforseo/competitor-finder/runs/${encodeURIComponent(runId)}/remove-row`,
      { domain, listType }
    );
  }
}
